import React from "react";
import Skeleton from "react-loading-skeleton";
import {
	Box,
	Button,
	Card,
	Center,
	Container,
	Flex,
	Group,
	SimpleGrid,
	Stack,
	Table,
	Text,
} from "@mantine/core";
import { useMutation, useQuery } from "@tanstack/react-query";

import { ThemedTitle, TitleContainer } from "@/components/HeaderContainer";
import { ThemedButton } from "@/components/ThemedButton";

import { useAuth } from "@/hooks/selectors/useAuth";

import authApi from "@/services/auth-api";
import generalApi from "@/services/general-api";

import "react-loading-skeleton/dist/skeleton.css";

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { CertFolderAnimation } from "./components/CertFolderAnimation";
import { BundleCertData, IndividualCertData } from "./ListingPage";

export default function ViewCert() {
	const [searchParams] = useSearchParams();
	const bundle_id = searchParams.get("bundle_id");
	const cert_id = searchParams.get("cert_id");

	const query = useQuery({
		queryKey: ["certification", bundle_id, cert_id],
		queryFn: () => {
			return authApi.getProfileBundleV2({
				bundle_id: bundle_id ? Number(bundle_id) : undefined,
				cert_id: cert_id ? Number(cert_id) : undefined,
			}) as Promise<BundleCertData | IndividualCertData>;
		},
	});

	if (query.isError) {
		return (
			<Center py={15}>
				<Text>Error loading certificate.</Text>
			</Center>
		);
	}

	return (
		<div>
			<TitleContainer>
				<ThemedTitle fz={20}>
					Certificate of{" "}
					{query.data?.bundle_name ? query.data?.bundle_name : query.data?.course_title}
				</ThemedTitle>
			</TitleContainer>

			<Box bg="white" py={20}>
				<Container size={"xl"} mb={50}>
					{query.isLoading && <LoadingSkeleton />}
					{query.data?.bundle_id && <BundleCertInfo data={query.data as BundleCertData} />}
					{query.data?.cert_id && !query.data?.bundle_id && (
						<IndividualCertInfo data={query.data as IndividualCertData} />
					)}
				</Container>
			</Box>
		</div>
	);
}

const IndividualCertInfo = ({ data }: { data: IndividualCertData }) => {
	const user = useAuth();
	const navigate = useNavigate();

	const purchaseHardCopyMutation = useMutation({
		mutationFn: () => {
			if (!data.cert_id) {
				return Promise.reject("No cert id");
			}
			return generalApi
				.requestExistingCert({
					cert_id: data.cert_id,
				})
				.then((res) => {
					window.location.href = res.checkout_url;
				});
		},
	});

	const handlePurchaseHardCopy = function () {
		purchaseHardCopyMutation.mutate();
	};

	const handleDownload = async () => {
		download(data.cert_url);
	};

	const handleView = () => {
		window.open(data.cert_url, "_blank");
	};

	const handleCopyVerificationLink = () => {
		navigator.clipboard.writeText(data.verification_url || "");
		toast.success("Verification link copied to clipboard");
	};

	return (
		<Flex gap={50} direction={{ base: "column-reverse", md: "row" }}>
			<Stack flex={"1 1 50%"} pt={40}>
				<Table border={0} withRowBorders={false} style={{ tableLayout: "fixed" }}>
					<Table.Tbody>
						{[
							{
								label: "Recipient",
								value: user.userData.user.learnermaster_name,
							},
							{
								label: "Trainer",
								value: data.trainer_name,
							},
							{
								label: "Course Title",
								value: data.course_title,
							},
							{
								label: "Course Ref",
								value: data.course_ref,
							},
							{
								label: "Certificate Serial Number",
								value: data.serial_no || "-",
							},
							{
								label: "Date of issue",
								value: data.issue_at,
							},
							{
								label: "Course Date",
								value:
									data.courserun_course_from +
									(data.courserun_course_from != data.courserun_course_to
										? " to " + data.courserun_course_to
										: ""),
							},
						].map((item, index) => {
							return (
								<Table.Tr key={index}>
									<Table.Td px={0} style={{ width: "200px", minWidth: "120px" }}>
										<Text size="sm" fw={"bold"}>
											{item.label}
										</Text>
									</Table.Td>
									<Table.Td px={0}>
										:
										<Text span ml={10} size="sm">
											{item.value}
										</Text>
									</Table.Td>
								</Table.Tr>
							);
						})}
					</Table.Tbody>
				</Table>
				<Text size="sm" mt={100}>
					For any further inquiries, please reach out to us at help@sda.edu.sg.
				</Text>
				<SimpleGrid cols={{ base: 1, md: 3 }}>
					{/* <ThemedButton onClick={handleView}>
					View
				</ThemedButton> */}
					{/* <ThemedButton onClick={handlePurchaseHardCopy} >
					Purchase Hardcopy
				</ThemedButton> */}
					<ThemedButton onClick={handleDownload}>Download PDF</ThemedButton>
					{data.verification_url ? (
						<ThemedButton
							variant="outline"
							onClick={handleCopyVerificationLink}
							leftSection={<i className="fa fa-copy" style={{ fontSize: 12 }} />}>
							Verification Link
						</ThemedButton>
					) : null}
					<ThemedButton variant="outline" onClick={() => navigate(-1)}>
						Back
					</ThemedButton>
				</SimpleGrid>
			</Stack>
			<Flex display={"flex"} flex={"1 1 50%"} ml={20} p={15}>
				<Box
					mx={"auto"}
					w={"100%"}
					maw={{
						base: "80%",
						md: "none",
					}}>
					<CertFolderAnimation width={"100%"} previewImage={data.cert_image_url || data.cert_url} />
				</Box>
			</Flex>
		</Flex>
	);
};

const BundleCertInfo = ({ data }: { data: BundleCertData }) => {
	const navigate = useNavigate();
	const user = useAuth();

	const checkoutMutation = useMutation({
		mutationFn: () => {
			if (!data.cert_id) {
				return Promise.reject("No cert id");
			}
			return generalApi
				.requestExistingCert({
					cert_id: data.cert_id,
				})
				.then((res) => {
					window.location.href = res.checkout_url;
				});
		},
	});

	const handlePurchaseHardCopy = function () {
		checkoutMutation.mutate();
	};

	const handleCopyVerificationLink = () => {
		navigator.clipboard.writeText(data.certData?.verification_url || "");
		toast.success("Verification link copied to clipboard");
	};

	return (
		<Card>
			<Flex gap={10} direction={{ base: "column-reverse", md: "row" }}>
				<Stack flex={"1 1 50%"} pt={30}>
					<Table border={0} withRowBorders={false} style={{ tableLayout: "fixed" }}>
						<Table.Tbody>
							{[
								{
									label: "Recipient",
									value: user.userData.user.learnermaster_name,
								},
							].map((item, index) => {
								return (
									<Table.Tr key={index}>
										<Table.Td p={0} style={{ width: "120px", minWidth: "120px" }}>
											<Text size="sm" fw={"bold"}>
												{item.label}
											</Text>
										</Table.Td>
										<Table.Td p={0}>
											:
											<Text span ml={10} size="sm">
												{item.value}
											</Text>
										</Table.Td>
									</Table.Tr>
								);
							})}
						</Table.Tbody>
					</Table>
					<Group>
						<Text size="sm" fw={"bold"}>
							Courses Completed
						</Text>
						{data.can_customize ? (
							<Button
								size="sm"
								variant="transparent"
								leftSection={<i className="fa fa-pencil " style={{ fontSize: 12 }} />}
								component={Link}
								to={`/professional-cert/create?cusotmize=1&count=${data.course_count}`}>
								<Text
									inherit
									style={{
										textDecoration: "underline",
										textUnderlineOffset: "3px",
										textDecorationThickness: "2px",
									}}>
									Edit
								</Text>
							</Button>
						) : null}
					</Group>
					<Stack gap={0}>
						{(data.certData?.cert_courses || []).map((course, index) => {
							return (
								<Box
									pt="10"
									style={{
										borderTop: index === 0 ? "1px solid #e0e0e0" : "none",
										borderBottom: "1px solid #e0e0e0",
									}}>
									<Text size="sm">{course.course_title}</Text>
									<div className="text-md text-muted flex flex-row justify-content-start gap-1 align-items-start flex-wrap">
										<Text size="sm" w={150}>
											{" "}
											{course.course_ref}
										</Text>
										<Text size="sm" w={220}>
											Completion Date: {course.courserun_course_to}
										</Text>
										<Text size="sm">Trainer: {course.trainer_name}</Text>
									</div>
								</Box>
							);
						})}
					</Stack>
					<Table border={0} withRowBorders={false} style={{ tableLayout: "fixed" }}>
						<Table.Tbody>
							{[
								{
									label: "Serial Number",
									value: data.certData.serial_no,
								},
								{
									label: "Certificate Issue Date",
									value: data.certData.issue_date,
								},
							].map((item, index) => {
								return (
									<Table.Tr key={index}>
										<Table.Td p={0} style={{ width: "200px" }}>
											<Text size="sm" fw={"bold"}>
												{item.label}
											</Text>
										</Table.Td>
										<Table.Td p={0}>
											:{" "}
											<Text span ml={10} size="sm">
												{item.value}
											</Text>
										</Table.Td>
									</Table.Tr>
								);
							})}
						</Table.Tbody>
					</Table>
					<Text size="sm">
						For any further inquiries, please reach out to us at help@sda.edu.sg.
					</Text>

					<SimpleGrid mt={100} cols={{ base: 1, md: 3 }}>
						{/* <ThemedButton
						disabled={!data.can_view}
						onClick={handlePurchaseHardCopy}
						loading={checkoutMutation.isPending}
					>
						Purchase Hardcopy
					</ThemedButton> */}
						<ThemedButton
							disabled={!data.can_view}
							onClick={() => {
								// window.open(data.certData?.cert_url, "_blank")
								if (data.certData?.cert_url) {
									download(data.certData?.cert_url);
								}
							}}>
							Download PDF
						</ThemedButton>
						{data.certData?.verification_url ? (
							<ThemedButton
								variant="outline"
								onClick={handleCopyVerificationLink}
								leftSection={<i className="fa fa-copy" style={{ fontSize: 12 }} />}>
								Verification Link
							</ThemedButton>
						) : null}
						<ThemedButton variant="outline" onClick={() => navigate(-1)}>
							Back
						</ThemedButton>
					</SimpleGrid>
				</Stack>
				<Box display={"flex"} flex={"1 1 50%"} ml={30} p={15} pb={50}>
					<Box
						mx={"auto"}
						w={"100%"}
						maw={{
							base: "80%",
							md: "none",
						}}>
						<CertFolderAnimation
							width={"100%"}
							previewImage={data.certData.cert_image_url || data.certData.cert_url}
						/>
					</Box>
				</Box>
			</Flex>
		</Card>
	);
};

const LoadingSkeleton = () => {
	return (
		<Flex direction={{ base: "column-reverse", md: "row" }}>
			<Flex flex={"1 1 50%"} direction={"column"} justify={"start"} pt={40} gap={20}>
				<Skeleton count={1} width={"70%"} height={30} />
				<Skeleton count={1} width={"80%"} height={30} />
				<Skeleton count={1} width={"80%"} height={30} />
				<Skeleton count={1} width={"80%"} height={30} />
				<Skeleton count={1} width={"80%"} height={30} />
				<Skeleton count={1} width={"80%"} height={30} />
			</Flex>
			<Box flex={"1 1 50%"} ta="center" p={40}>
				<Skeleton enableAnimation={true} style={{ aspectRatio: 3.2 / 4 }} width={"100%"} />
			</Box>
		</Flex>
	);
};

async function download(url: string) {
	try {
		// Method 1: Try to fetch as blob and create download link
		const response = await fetch(url);
		// const response = await fetch("https://tms.fcacademy.com.sg/jtcpdf/report/fcacert/TSST__Jack_Ng_Wei_Ming_2023-09-23.pdf")
		const blob = await response.blob();

		const href = document.createElement("a");
		const url = window.URL.createObjectURL(blob);
		href.href = url;
		href.download = `certificate.pdf`;

		href.target = "_blank";
		document.body.appendChild(href); // Required for Firefox
		href.click();
		document.body.removeChild(href);
		window.URL.revokeObjectURL(url); // Clean up
	} catch (error) {
		console.error("Download failed, trying fallback method:", error);
		// Fallback method: traditional download with target attribute
		const href = document.createElement("a");
		href.href = url;
		href.download = `certificate.pdf`;
		href.target = "_self"; // Prevent new tab
		href.rel = "noopener noreferrer";
		document.body.appendChild(href);
		href.click();
		document.body.removeChild(href);
	}
}
